import { Textarea } from "@/components/podkit/forms/Textarea";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { useSendToAgentExecution, type PlainAgentExecution } from "@/queries/agent-queries";
import {
    SendToAgentExecutionRequest,
    UserInputBlock,
    UserInputBlock_TextInput,
} from "gitpod-next-api/gitpod/v1/agent_pb";
import { useCallback, type FC } from "react";
import { v4 as uuidv4 } from "uuid";
import { usePersistedInput } from "@/hooks/use-persisted-input";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";

export type NewPendingMessage = {
    id: string;
    // If content is undefined, it means the message with `id` failed to be sent
    // and should be retracted again.
    content?: string;
};

export type ChatInputProps = {
    agentExecution: PlainAgentExecution;
    placeholder?: string;
    onNewMessage: (message: NewPendingMessage) => void;
} & PropsWithClassName;

export const ChatInput: FC<ChatInputProps> = ({ agentExecution, onNewMessage, className, placeholder }) => {
    const sendToAgentExecution = useSendToAgentExecution();
    const { toast } = useToast();

    const storageKey = `agent-execution-input-${agentExecution.id}`;
    const [inputValue, setInputValue] = usePersistedInput(storageKey, "");

    const onSubmit = useCallback(
        async (text: string) => {
            if (!agentExecution.id) {
                return;
            }
            await sendToAgentExecution.mutateAsync(
                new SendToAgentExecutionRequest({
                    agentExecutionId: agentExecution.id,
                    userInput: new UserInputBlock({
                        input: {
                            case: "text",
                            value: new UserInputBlock_TextInput({
                                content: text,
                            }),
                        },
                    }),
                }),
            );
        },
        [agentExecution.id, sendToAgentExecution],
    );

    const onKeyDown = useCallback(
        async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === "Enter" && !e.shiftKey) {
                const input = e.target as HTMLTextAreaElement;
                e.preventDefault();
                e.stopPropagation();
                const value = input.value;
                const id = uuidv4();
                try {
                    setInputValue("");
                    onNewMessage({ id, content: value });
                    await onSubmit(value);
                } catch (error) {
                    setInputValue(value);
                    onNewMessage({ id, content: undefined });
                    toast({
                        title: "Error sending to agent execution",
                        description: formatError(error),
                    });
                }
                return;
            }
        },
        [onSubmit, toast, onNewMessage, setInputValue],
    );

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setInputValue(e.target.value);
    };

    return (
        <Textarea
            maxRows={15}
            placeholder={placeholder}
            onKeyDown={onKeyDown}
            onChange={handleChange}
            value={inputValue}
            className={cn("w-full resize-none rounded-xl bg-surface-pure p-4", className)}
            data-testid="agent-execution-chat-input"
        />
    );
};
